import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import './FadeScrollWrapper.scss';
import { _className, ComponentSync } from '@nu-art/thunderstorm-frontend/index';
import { Debounce } from '@nu-art/ts-common';
export class FadeScrollWrapper extends ComponentSync {
    //######################### Class Properties #########################
    // max fade band size, as a % of the container, shown at a fully-scrolled-away edge
    static FADE_MAX_PERCENT = 10;
    // px distance from an edge over which the band ramps from 0 to FADE_MAX_PERCENT
    static FADE_RAMP_PX = 48;
    static defaultProps = {
        direction: 'vertical'
    };
    fadeRef = React.createRef();
    componentRef = React.createRef();
    // per-instance fallback ref; never create refs in defaultProps (shared across all instances)
    internalScrollRef = React.createRef();
    fadePos = {
        top: 0,
        bottom: 100,
    };
    debounce_UpdateFade = new Debounce(() => {
        this.calculateFadePosition();
        this.updateFade();
    }, 100, 300);
    observer = new ResizeObserver(() => this.debounce_UpdateFade.trigger());
    //######################### Life Cycle #########################
    get scrollRef() {
        return this.props.scrollRef ?? this.internalScrollRef;
    }
    __onScrollBarUpdate(ref) {
        if (ref !== this.scrollRef)
            return;
        this.debounce_UpdateFade.trigger();
    }
    componentDidMount() {
        const element = this.componentRef.current;
        if (element)
            this.observer.observe(element);
        this.debounce_UpdateFade.trigger();
    }
    componentWillUnmount() {
        this.observer.disconnect();
    }
    shouldComponentUpdate() {
        return true;
    }
    //######################### Logic #########################
    calculateFadePosition = () => {
        const content = this.scrollRef.current;
        if (!content)
            return;
        const isHorizontal = this.props.direction === 'horizontal';
        const scroll = isHorizontal ? content.scrollLeft : content.scrollTop;
        const maxScroll = (isHorizontal ? content.scrollWidth : content.scrollHeight)
            - (isHorizontal ? content.clientWidth : content.clientHeight);
        // nothing to scroll -> no fade on either edge
        if (maxScroll <= 0) {
            this.fadePos.top = 0;
            this.fadePos.bottom = 100;
            return;
        }
        // grow each fade band from the pixel distance to its own edge, capped at FADE_MAX_PERCENT.
        // pixel-based (not % of total scroll) so the ramp is consistent regardless of content length.
        const { FADE_MAX_PERCENT: max, FADE_RAMP_PX: ramp } = FadeScrollWrapper;
        this.fadePos.top = Math.min(scroll / ramp, 1) * max;
        this.fadePos.bottom = 100 - Math.min((maxScroll - scroll) / ramp, 1) * max;
    };
    onWheel = (e) => {
        if (this.props.direction !== 'horizontal')
            return;
        if (!e.deltaY)
            return;
        // Translate the vertical scroll (deltaY) into horizontal scroll
        e.currentTarget.scrollBy({
            left: e.deltaY, // Scroll horizontally by the vertical scroll amount
        });
    };
    updateFade = () => {
        const fade = this.fadeRef.current;
        if (!fade)
            return;
        fade.style.setProperty('--fade-top-pos', `${this.fadePos.top}%`);
        fade.style.setProperty('--fade-bottom-pos', `${this.fadePos.bottom}%`);
    };
    //######################### Render #########################
    render() {
        const styles = {
            '--fade-top-pos': `${this.fadePos.top}%`,
            '--fade-bottom-pos': `${this.fadePos.bottom}%`,
        };
        const className = _className('fade-scroll-wrapper', this.props.className);
        return _jsxs("div", { className: className, ref: this.componentRef, onCopy: this.props.onCopy, children: [_jsx("div", { className: 'fade-scroll-wrapper__content', ref: this.scrollRef, onScroll: () => {
                        this.calculateFadePosition();
                        this.updateFade();
                    }, onWheel: this.onWheel, children: this.props.children }), _jsx("div", { className: _className('fade-scroll-wrapper__fade', this.props.direction), ref: this.fadeRef, style: styles })] });
    }
}
//# sourceMappingURL=FadeScrollWrapper.js.map